import React, { useContext } from 'react';
import { Stack, Checkbox, Toggle, IToggleStyles, getTheme, IStyle } from '@fluentui/react';
import { ContentType, SubjectImagePreferences, SubjectJsonPreferences } from 'services';
import { ReviewContext } from 'components/review/ReviewContextProvider';
import { ReviewTimer } from './ReviewTimer';
export interface CommandBarProps {
  showSelectAll: boolean;
  selectAllValue: boolean;
  onSelectAllClick: (checked: boolean) => void;
  contentType: ContentType;
  subjectPreferences: SubjectImagePreferences & SubjectJsonPreferences;
  onFilterValueChange: (state: SubjectImagePreferences & SubjectJsonPreferences) => void;
  timeLeftInSeconds: number;
}

function renderContentPreference(
  contentType: ContentType,
  value: SubjectImagePreferences & SubjectJsonPreferences,
  changeCallback: (state: SubjectImagePreferences & SubjectJsonPreferences) => void,
  textStyle: IStyle
): JSX.Element {
  const toggleStyle: Partial<IToggleStyles> = { text: textStyle };
  const { state, dispatch } = useContext(ReviewContext);
  switch (contentType) {
    case ContentType.json:
      return (
        <>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={!!(value.isDefaultBlur || value.isTextBlurred)}
              onText="Blur"
              offText="Blur"
              onChange={(_, checked?: boolean): void =>
                changeCallback({ ...value, isDefaultBlur: !!checked, isTextBlurred: !!checked })
              }
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultGrayscale}
              onText="Desaturate"
              offText="Desaturate"
              onChange={(_, checked?: boolean): void => changeCallback({ ...value, isDefaultGrayscale: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultFlipped}
              onText="Flip"
              offText="Flip"
              onChange={(_, checked?: boolean): void => changeCallback({ ...value, isDefaultFlipped: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultMirrored}
              onText="Mirror"
              offText="Mirror"
              onChange={(_, checked?: boolean): void => changeCallback({ ...value, isDefaultMirrored: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          {value.animationLoopIsActive && (
            <Stack.Item>
              <Toggle
                className="p-0 m-0"
                checked={state.carouselAnimationEnabled}
                onText="Pause animation"
                offText="Play animation"
                onChange={(_, checked?: boolean): void => {
                  if (checked !== undefined) {
                    if (checked) {
                      dispatch({ type: 'ENABLE_CAROUSEL_ANIMATION' });
                    } else {
                      dispatch({ type: 'DISABLE_CAROUSEL_ANIMATION' });
                    }
                  }
                }}
                styles={toggleStyle}
              />
            </Stack.Item>
          )}
        </>
      );
    case ContentType.image:
      return (
        <>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultBlur}
              onText="Blur images"
              offText="Blur images"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultBlur: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultGrayscale}
              onText="Desaturate images"
              offText="Desaturate images"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultGrayscale: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultFlipped}
              onText="Flip images"
              offText="Flip images"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultFlipped: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultMirrored}
              onText="Mirror"
              offText="Mirror"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultMirrored: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
        </>
      );
    case ContentType.video:
      return (
        <>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultBlur}
              onText="Blur"
              offText="Blur"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultBlur: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultGrayscale}
              onText="Desaturate"
              offText="Desaturate"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultGrayscale: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultFlipped}
              onText="Flip"
              offText="Flip"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultFlipped: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
          <Stack.Item>
            <Toggle
              className="p-0 m-0"
              checked={value.isDefaultMirrored}
              onText="Mirror"
              offText="Mirror"
              onChange={(event, checked?: boolean): void => changeCallback({ ...value, isDefaultMirrored: !!checked })}
              styles={toggleStyle}
            />
          </Stack.Item>
        </>
      );
    case ContentType.text:
      return (
        <Stack.Item>
          <Toggle
            className="p-0 m-0"
            checked={value.isTextBlurred}
            onText="Blur"
            offText="Blur"
            onChange={(event, checked?: boolean): void => changeCallback({ ...value, isTextBlurred: !!checked })}
            styles={toggleStyle}
          />
        </Stack.Item>
      );
    default:
      return <></>;
  }
}

export const CommandBar: React.FunctionComponent<CommandBarProps> = (props: CommandBarProps) => {
  const { fonts } = getTheme();
  const textStyle: IStyle = {
    fontFamily: fonts.mediumPlus.fontFamily,
    fontSize: fonts.mediumPlus.fontSize,
    lineHeight: fonts.mediumPlus.lineHeight
  };
  const selectAllChange = (ev, checked?: boolean): void => {
    props.onSelectAllClick(!!checked);
  };
  return (
    <Stack horizontal horizontalAlign="center" verticalAlign="center" className="py-3 pl-5 pr-3 ml-2">
      {props.showSelectAll && (
        <Stack.Item className="pr-4">
          <Checkbox
            label="Select all"
            className="p-0 m-0"
            onChange={selectAllChange}
            checked={props.selectAllValue}
            styles={{
              label: { margin: 0 },
              text: textStyle
            }}
          />
        </Stack.Item>
      )}
      <Stack.Item grow>
        <Stack horizontal>
          {renderContentPreference(props.contentType, props.subjectPreferences, props.onFilterValueChange, textStyle)}
        </Stack>
      </Stack.Item>
      <ReviewTimer timeLeftInSeconds={props.timeLeftInSeconds} />
    </Stack>
  );
};

export default CommandBar;
